<script setup lang="ts">
import { type PropType, computed, defineProps, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import BScroll from '@better-scroll/core'
import couponCard from './couponCard.vue'
import couponDetail from './couponDetail.vue'
import { removeUserInfo } from './utils'
import { couponTrack } from './helper'
import { adsPositionQueryPositionPost, adsPositionSubmitPositionPost } from '~/services/leafyApi/AdsPositionApi'
import type { ReturnPromise } from '~/services/leafyApi/AdsPopupApi'

type AdsPositionQueryPositionPostType = NonNullable<
  ReturnPromise<typeof adsPositionQueryPositionPost>
>

const props = defineProps({
  pageType: {
    type: String,
    default: '',
  },
  /** 优惠券的渠道，每个渠道不一样 */
  channel: {
    type: String,
    default: '',
  },
  /** 优惠券 */
  couponsInfo: {
    type: Object as PropType<AdsPositionQueryPositionPostType['couponPosition']>,
    default: () => {},
  },
})

const emit = defineEmits(['fresh'])

defineOptions({
  name: 'NewCouponsList',
})

const listBox = ref<HTMLElement | null>(null)

const { locale } = useI18n()

let scrollLeft = 0

// 是否领取过
const saved = ref(false)

const couponsInfo = computed(() => {
  return props.couponsInfo || {}
})

const showDetail = ref(false)
const detailInfo = ref(null)
/**
 *
 * 是否所有卡券都被领取了
 *
 *  */
const isGetAllTicket = computed(() => {
  return couponsInfo.value?.itemList?.every((item: any) => +item.status === 1)
})

let bs: any

function emitDetail(e: any) {
  detailInfo.value = {
    ...e,
    left: e.left - scrollLeft, // - listBoxPos?.left,
    copySuccessText: couponsInfo.value?.copySuccessText,
  }
  showDetail.value = true
}
/** refresh */
function refresh() {
  bs?.refresh()
}

defineExpose({
  refresh,
})
/** 领券埋点 */
function trackEvent(PromoCode: (string | undefined)[], isOk: string) {
  couponTrack({
    eventid: 12048,
    action: 'click',
    label: 'OperatingPromoCode',
    value: { PageName: props.pageType, PromoCode, PromoCodeState: isOk },
  })
}
// 先去登录
function toLogin() {
  location.href = `/${locale.value}/passport/signin?pageUrl=${encodeURIComponent(location.href)}`
}
/** 单个获取优惠券 */
function hanldeGetCoupon({ couponPostData, batchNo }: { couponPostData: any; batchNo: string }) {
  const token = useCookie('sectoken')
  if (!token.value) {
    return toLogin()
  }
  adsPositionSubmitPositionPost(couponPostData, {
    responseOnlyBody: false,
  })
    .then((result) => {
      if (result?.status === 200) {
        emit('fresh')
        ElMessage.success(result?.message)
        trackEvent([batchNo], '1')
      }
      else {
        throw result
      }
    }).catch((err: any) => {
      trackEvent([batchNo], '0')
      console.error('领取失败', err)
      if (err.status === 401) {
        removeUserInfo()
        toLogin()
      }
    })
}

/** 获取所有优惠券 */
function getAllCouponsHandler() {
  const token = useCookie('sectoken')
  if (!token.value) {
    return location.href = `/${locale.value}/passport/signin?pageUrl=${encodeURIComponent(location.href)}`
  }
  if (isGetAllTicket.value) {
    return
  }

  const batchList = (couponsInfo.value?.itemList || [])?.filter(item => +(item?.status || 0) !== 1).map(item => item.batchNo)
  /** 优惠券数据 */
  const couponPostData = {
    pageType: props.pageType || '',
    couponActivityType: {
      configId: couponsInfo.value?.configId as string,
      couponList: batchList as string[],
    },
  }
  adsPositionSubmitPositionPost(couponPostData, {
    responseOnlyBody: false,
  })
    .then((result) => {
      if (result?.status === 200) {
        emit('fresh')
        ElMessage.success(result?.message)
        trackEvent(batchList, '1')
      }
      else {
        throw result
      }
    }).catch((err: any) => {
      trackEvent(batchList, '0')
      console.error('领取失败', err)
      if (err.status === 401) {
        removeUserInfo()
        toLogin()
      }
    })
}

onMounted(() => {
  // 事件委托
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    if (!target?.matches('.detail-box, .detail-box *, .detail-ctrl') && showDetail.value) {
      // 执行相应操作
      showDetail.value = false
      detailInfo.value = null
    }
  })

  nextTick(() => {
    setTimeout(() => {
      bs = new BScroll('.main-coupon-list', {
        scrollX: true,
        scrollY: false,
        bounce: false,
        click: true,
        // bindToWrapper: false,
        disableTouch: true,
        disableMouse: false,
      })
      bs.on('beforeScrollStart', () => {
        showDetail.value = false
        detailInfo.value = null
      })
      bs.on('scrollEnd', () => {
        scrollLeft = -bs.x
      })
    }, 2000)
  })
})
</script>

<template>
  <div
    ref="listBox" class="coupons-list" :style="{
      display: couponsInfo.itemList?.length ? 'block' : 'none',
      backgroundImage: `${couponsInfo.bgImg && `url(${couponsInfo.bgImg})`}`,
    }"
  >
    <div class="title-box flex">
      <p class="title bold f28 flex-1">
        {{ couponsInfo.title }}
      </p>
      <p
        v-if="!saved" class="font-bold cursor-pointer " :class="{
          'claim-btn': !isGetAllTicket,
          'claim-txt': isGetAllTicket,
        }" @click="getAllCouponsHandler"
      >
        {{ couponsInfo.buttonText }}
      </p>
    </div>
    <div class="list main-coupon-list overflow-hidden w-full">
      <div class="list-box inline-flex">
        <couponCard
          v-for="item in couponsInfo.itemList" :key="item.batchNo" :config-id="couponsInfo.configId"
          :page-name="props.pageType" :detail-btn-txt="couponsInfo.detailText" :info="item" :channel="channel"
          :pop-call-back="true"
          @get-coupon="hanldeGetCoupon"
          @emit-detail="emitDetail"
        />
      </div>
    </div>
    <couponDetail v-if="showDetail" :info="detailInfo || {}" :copy-txt="couponsInfo.copyText" />
  </div>
</template>

<style lang="scss" scoped>
@tailwind base;
@tailwind components;
@tailwind utilities;

.bold {
  font-weight: bold;
}

.flex {
  display: flex;
  align-items: center;
}

.no-warp {
  white-space: nowrap;
}

.flex-1 {
  flex: 1;
}

.f28 {
  font-size: 28px;
}

.f16 {
  font-size: 16px;
}

.f12 {
  font-size: 12px;
}

.gray {
  color: #666;
}

.green {
  color: #00C868;
}

.coupons-list {
  width: 1190px;
  margin: 38px auto 0;
  padding: 24px;
  background-color: #00C868;
  // height: 244px;
  border-radius: 16px;
  box-sizing: border-box;
  position: relative;
  z-index: 2;
  background-position: top right;
  background-repeat: no-repeat;
  background-size: auto 100%;
}

.title-box {
  margin-bottom: 24px;
  line-height: 32px;

  .title {
    color: #FAFF00;
  }
}

.list {
  .list-box {
    // padding:0 16px;
    // height: 140px;
    flex-wrap: nowrap;
    // overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE 10+ */
    &::-webkit-scrollbar {
      display: none; /* Chrome Safari */
    }
  }
}

.main-coupon-list {
  overflow-x: hidden;
}

.claim-btn {
  height: 28px;
  line-height: 28px;
  padding: 0 16px;
  background-color: #FAFF00;
  border-radius: 14px;
  font-weight: bold;
}

.claim-txt {
  padding-left: 28px;
  background: url(../../assets/img/coupons/done.png) left center / 20px 20px no-repeat;
  height: 28px;
  line-height: 28px;
  color: #FAFF00;
}
</style>
